import React, { useEffect, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { CircularProgress, Typography } from "@mui/material";
import ReactPaginate from "react-paginate";

import { DetailModal } from "./DetailModal";
import { IDataTableProps } from "./interface";
import * as Styled from "./styles";
import backArrow from "../../assets/svg/bx-arrow-back.svg";

const Arrow: React.FC<{ rotated: boolean }> = ({ rotated }) => {
  return (
    <img
      style={{
        transform: rotated ? "rotate(180deg)" : "none",
      }}
      src={backArrow}
      alt=""
    />
  );
};

export const DataTable: React.FC<IDataTableProps> = ({
  columns,
  rows,
  loading = false,
  emptyMessage = "Nenhum dado encontrado",
  maxHeight = "60vh",
  stickyHeader = true,
  striped = true,
  pagination = true,
  defaultItemsPerPage = 5,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: "", content: "" });
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [rows?.length, defaultItemsPerPage]);

  const offset = currentPage * defaultItemsPerPage;
  const currentPageData =
    pagination && Array.isArray(rows)
      ? rows?.slice(offset, offset + defaultItemsPerPage)
      : rows || [];
  const pageCount =
    pagination && rows ? Math.ceil(rows.length / defaultItemsPerPage) : 0;

  const handlePageClick = (selectedPage: number) => {
    setCurrentPage(selectedPage);
  };

  const handleOpenModal = (title: string, content: string) => {
    setModalContent({ title, content });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const renderCellContent = (
    value: any,
    column: any,
    shouldUseEllipsis: boolean,
  ) => {
    if (column.format) {
      return column.format(value);
    }

    const displayValue = value ?? "N/A";
    const stringValue = String(displayValue);

    if (shouldUseEllipsis) {
      return (
        <Styled.EllipsisCell>
          <Styled.EllipsisText
            title={stringValue}
            maxWidth={column.ellipsisMaxWidth}
          >
            {stringValue}
          </Styled.EllipsisText>
          <Styled.ViewMoreButton
            onClick={() => handleOpenModal(column.label, stringValue)}
          >
            <SearchIcon />
          </Styled.ViewMoreButton>
        </Styled.EllipsisCell>
      );
    }

    return displayValue;
  };

  if (loading) {
    return (
      <Styled.LoadingContainer>
        <CircularProgress />
      </Styled.LoadingContainer>
    );
  }

  if (!rows || rows.length === 0 || !Array.isArray(rows)) {
    return (
      <Styled.EmptyContainer>
        <Typography variant="h6" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Styled.EmptyContainer>
    );
  }

  return (
    <>
      <Styled.TableWrapper>
        {/* Header separado */}
        <Styled.HeaderRow>
          {columns.map((column) => (
            <Styled.HeaderCell
              key={column.id}
              width={column.width}
              align={column.align || "left"}
            >
              {column.label}
            </Styled.HeaderCell>
          ))}
        </Styled.HeaderRow>

        {/* Container de cards */}
        <Styled.CardsContainer maxHeight={maxHeight}>
          {Array.isArray(currentPageData) &&
            currentPageData.map((row, rowIndex) => (
              <Styled.CardRow key={rowIndex}>
                {columns.map((column) => {
                  const value = row[column.id];
                  return (
                    <Styled.CardCell
                      key={column.id}
                      width={column.width}
                      align={column.align || "left"}
                    >
                      {renderCellContent(
                        value,
                        column,
                        column.useEllipsis || false,
                      )}
                    </Styled.CardCell>
                  );
                })}
              </Styled.CardRow>
            ))}
        </Styled.CardsContainer>

        {/* Paginação */}
        {pagination && pageCount > 1 && (
          <Styled.PaginationContainer>
            <ReactPaginate
              previousLabel={
                currentPage === 0 ? null : <Arrow rotated={false} />
              }
              nextLabel={
                currentPage === pageCount - 1 ? null : <Arrow rotated={true} />
              }
              pageCount={pageCount}
              onPageChange={(e: any) => handlePageClick(e.selected)}
              forcePage={currentPage}
              containerClassName={"pagination"}
              previousLinkClassName={
                currentPage === 0
                  ? "previusAndNextDisableCursor"
                  : "pagination__link"
              }
              nextLinkClassName={
                currentPage === pageCount - 1
                  ? "previusAndNextDisableCursor"
                  : "pagination__link"
              }
              disabledClassName={"pagination__link--disabled"}
              activeClassName={"pagination__link--active"}
            />
          </Styled.PaginationContainer>
        )}
      </Styled.TableWrapper>
      <DetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalContent.title}
        content={modalContent.content}
      />
    </>
  );
};

export default DataTable;
